import { formatDateRu, formatKg } from '@pms/shared';
import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAuthStore } from '../auth/store.js';
import { PHOTO_KINDS, PHOTO_KIND_NAMES } from '@pms/shared';
import { PhotoCapture } from '../components/PhotoCapture.js';
import { IncidentsStep } from '../reports/IncidentsStep.js';
import { NumbersStep } from '../reports/NumbersStep.js';
import { saveDraft, type ReportDraft } from '../reports/draft.js';
import { submitDraft, validateDraft } from '../reports/submit.js';
import { db } from '../sync/db.js';

/**
 * Мастер сдачи смены: цифры → события → фото → подтверждение.
 *
 * Черновик пишется в локальную базу после каждого изменения, поэтому выйти
 * и вернуться можно на любом шаге. Отправка — одно событие в очередь,
 * ожидания сервера нет.
 */

const STEPS = ['Цифры', 'События смены', 'Фото', 'Проверка'];

export function ReportWizardScreen(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const [sending, setSending] = useState(false);

  const draft = useLiveQuery(async () => (id ? db.drafts.get(id) : undefined), [id]);
  const site = useLiveQuery(
    async () => (draft ? db.sites.get(draft.siteId) : undefined),
    [draft?.siteId],
  );
  const machine = useLiveQuery(
    async () => (draft ? db.machines.get(draft.machineId) : undefined),
    [draft?.machineId],
  );
  const sizes = useLiveQuery(() => db.productSizes.orderBy('sortOrder').toArray(), [], []);
  const materials = useLiveQuery(() => db.materialTypes.orderBy('sortOrder').toArray(), [], []);
  const people = useLiveQuery(() => db.users.toArray(), [], []);
  const photos = useLiveQuery(
    async () => (draft ? db.photos.where('reportId').equals(draft.id).toArray() : []),
    [draft?.id],
    [],
  );

  // Снимки живут в своей очереди — переносим их в черновик вместе с назначением,
  // чтобы отчёт знал, какие фото к нему относятся и что на них
  const photoRefs = photos.map((photo) => ({ id: photo.id, kind: photo.kind }));
  const photoKey = photoRefs.map((photo) => `${photo.id}:${photo.kind}`).join(',');
  useEffect(() => {
    if (!draft) return;
    const current = draft.photos.map((photo) => `${photo.id}:${photo.kind}`).join(',');
    if (current === photoKey) return;
    void saveDraft({ ...draft, photos: photoRefs });
  }, [draft, photoKey]);

  if (!draft || !site || !machine) {
    return <p className="p-6 text-lg text-dim">Черновик не найден</p>;
  }

  const patch = (change: Partial<ReportDraft>): void => {
    void saveDraft({ ...draft, ...change });
  };

  const goto = (step: number): void => patch({ step: Math.max(0, Math.min(step, STEPS.length - 1)) });

  const problems = validateDraft({ ...draft, photos: photoRefs });

  const send = async (): Promise<void> => {
    if (!user) return;
    setSending(true);
    try {
      await submitDraft({ ...draft, photos: photoRefs }, user.id);
      if (navigator.vibrate) navigator.vibrate([40, 60, 40]);
      navigate('/', { replace: true });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen px-4 py-6">
      <header className="mb-4">
        <div className="mb-2 flex items-center justify-between gap-3">
          <h1 className="text-xl font-semibold">{site.name}</h1>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="rounded-xl bg-muted px-4 py-3 text-base font-semibold"
          >
            Выйти
          </button>
        </div>
        <p className="text-base text-dim">
          {machine.name} · смена {draft.shiftNumber} за {formatDateRu(draft.date)}
          {draft.participants.length > 1 && ' · вдвоём'}
        </p>
      </header>

      <ol className="mb-6 flex gap-2">
        {STEPS.map((title, index) => (
          <li key={title} className="flex-1">
            <button
              type="button"
              onClick={() => goto(index)}
              className={`w-full rounded-lg px-2 py-2 text-base font-semibold ${
                index === draft.step
                  ? 'bg-sky-600 text-white'
                  : index < draft.step
                    ? 'bg-emerald-800 text-white'
                    : 'bg-card shadow-card text-dim'
              }`}
            >
              {index + 1}
            </button>
          </li>
        ))}
      </ol>

      <h2 className="mb-4 text-lg font-semibold">{STEPS[draft.step]}</h2>

      {draft.step === 0 && (
        <NumbersStep draft={draft} siteCode={site.code} onChange={patch} />
      )}

      {draft.step === 1 && (
        <IncidentsStep draft={draft} siteCode={site.code} onChange={patch} />
      )}

      {draft.step === 2 && (
        <div className="flex flex-col gap-6">
          {/* Два снимка отвечают на разные вопросы: что сделали и в каком виде
              оставили рабочее место. Поэтому и блока два, а не один список */}
          {PHOTO_KINDS.map((kind) => (
            <section key={kind}>
              <h2 className="mb-2 text-lg font-semibold">{PHOTO_KIND_NAMES[kind]}</h2>
              <PhotoCapture
                reportId={draft.id}
                kind={kind}
                siteId={draft.siteId}
                siteName={site.name}
                shiftNumber={draft.shiftNumber}
                machineId={draft.machineId}
                machineName={machine.name}
              />
            </section>
          ))}
          <p className="text-base text-dim">
            Нужны оба снимка. Отчёт уйдёт сразу, а фотографии догрузятся сами —
            даже если связь появится только к концу смены.
          </p>
        </div>
      )}

      {draft.step === 3 && (
        <div className="flex flex-col gap-4">
          <section className="rounded-xl bg-card shadow-card p-4">
            <h3 className="mb-3 text-lg font-semibold">Выработка</h3>
            {Object.entries(draft.outputs).length === 0 &&
              Object.entries(draft.materials).length === 0 && (
                <p className="text-base text-dim">Ничего не введено</p>
              )}
            <ul className="flex flex-col gap-2 text-base">
              {Object.entries(draft.outputs).map(([sizeId, qty]) => (
                <li key={sizeId} className="flex justify-between">
                  <span className="text-body">
                    {sizes.find((size) => size.id === sizeId)?.name ?? sizeId}
                  </span>
                  <span className="text-counter tabular-nums">{qty} шт</span>
                </li>
              ))}
              {draft.batchesCount !== null && (
                <li className="flex justify-between">
                  <span className="text-body">Замесов</span>
                  <span className="text-counter tabular-nums">{draft.batchesCount}</span>
                </li>
              )}
              {Object.entries(draft.materials).map(([materialId, entry]) => (
                <li key={materialId} className="flex justify-between">
                  <span className="text-body">
                    {entry.direction === 'PRODUCED' ? 'Произведено' : 'Расход'}:{' '}
                    {materials.find((material) => material.id === materialId)?.name ?? materialId}
                  </span>
                  <span className="text-counter tabular-nums">{formatKg(entry.qtyG)} кг</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="rounded-xl bg-card shadow-card p-4">
            <h3 className="mb-3 text-lg font-semibold">События смены</h3>
            {draft.incidents.length === 0 ? (
              <p className="text-base text-dim">Не отмечены</p>
            ) : (
              <ul className="flex flex-col gap-2 text-base text-body">
                {draft.incidents.map((incident) => (
                  <li key={incident.kind}>
                    {incident.kind === 'STARTUP' && 'Запуск'}
                    {incident.kind === 'PURGE' && 'Выгонка'}
                    {incident.kind === 'REPAIR' && 'Ремонт'}
                    {incident.kind === 'TRAINING' && 'Обучение'}
                    {incident.durationMinutes ? ` — ${incident.durationMinutes} мин` : ''}
                    {incident.traineeUserId &&
                      ` — ${people.find((person) => person.id === incident.traineeUserId)?.fullName ?? ''}`}
                  </li>
                ))}
              </ul>
            )}
            {draft.comment && <p className="mt-3 text-base text-body">{draft.comment}</p>}
          </section>

          <section className="rounded-xl bg-card shadow-card p-4">
            <h3 className="mb-2 text-lg font-semibold">Фото</h3>
            <p className="text-base text-body">
              {photoRefs.length === 0
                ? 'не прикреплено'
                : photoRefs.map((photo) => PHOTO_KIND_NAMES[photo.kind]).join(', ')}
            </p>
          </section>

          {problems.length > 0 && (
            <section className="rounded-xl bg-rose-50 text-rose-800 ring-1 ring-rose-200 p-4">
              <h3 className="mb-2 text-lg font-semibold">Нужно поправить</h3>
              <ul className="flex flex-col gap-1 text-base">
                {problems.map((problem) => (
                  <li key={problem}>· {problem}</li>
                ))}
              </ul>
            </section>
          )}

          <button
            type="button"
            disabled={problems.length > 0 || sending}
            onClick={() => void send()}
            className="rounded-xl bg-emerald-600 px-5 py-5 text-lg font-semibold text-white active:bg-emerald-700 disabled:opacity-40"
          >
            {sending ? 'Отправляю…' : 'Сдать смену'}
          </button>
        </div>
      )}

      <div className="mt-6 flex gap-touch">
        <button
          type="button"
          disabled={draft.step === 0}
          onClick={() => goto(draft.step - 1)}
          className="flex-1 rounded-xl bg-muted px-5 py-4 text-lg font-semibold active:bg-line disabled:opacity-40"
        >
          Назад
        </button>
        <button
          type="button"
          disabled={draft.step === STEPS.length - 1}
          onClick={() => goto(draft.step + 1)}
          className="flex-1 rounded-xl bg-sky-600 px-5 py-4 text-lg font-semibold text-white active:bg-sky-700 disabled:opacity-40"
        >
          Далее
        </button>
      </div>
    </div>
  );
}
